import { create } from "zustand";

/**
 * Where you have been in this tab, so the header's ‹ › arrows can say whether
 * there is anywhere to go.
 *
 * `history.back()` is easy; knowing whether it would *do* anything is not. The
 * browser exposes `history.length` (which counts entries from before the app
 * loaded) and nothing at all about forward availability, so a pair of arrows
 * driven by the platform alone would either always look enabled or lie. This
 * module keeps the one thing the platform withholds: an ordered list of the
 * hrefs this app has visited and where in it we currently stand.
 *
 * The list is reconciled against TanStack's `__TSR_index` — the counter it
 * stamps into `history.state` on every push — rather than inferred from the
 * order navigations arrive in. That is what makes a browser back button, a
 * gesture swipe, and a reload land on the same index we would have computed
 * ourselves, and it is why the state survives in `sessionStorage`: after F5 the
 * entries are gone from memory but the browser's stack (and its index) is not.
 */

export const NAVIGATION_HISTORY_STORAGE_KEY = "rivets.nav-history.v1";

/**
 * Fifty entries is far past any real "go back" intent and keeps the serialized
 * blob a few kilobytes. Trimming happens at the *old* end, so the arrow you are
 * about to press is never the one that was dropped.
 */
export const NAVIGATION_HISTORY_LIMIT = 50;

export interface NavigationHistory {
  /** Visited hrefs, oldest first. */
  entries: string[];
  /** Index into `entries` of the entry showing now; -1 before the first visit. */
  index: number;
  /**
   * The browser history index (`__TSR_index`) that `entries[0]` sits at. Zero
   * until the cap trims the front, after which it absorbs the drift so the
   * mapping from a browser index to a slot stays a subtraction.
   */
  base: number;
}

export const EMPTY_NAVIGATION_HISTORY: NavigationHistory = { entries: [], index: -1, base: 0 };

/**
 * `base > 0` means the cap dropped entries the browser can still walk back to,
 * so the arrow stays live even though our own list has run out.
 */
export function canGoBack(state: NavigationHistory): boolean {
  return state.index > 0 || (state.base > 0 && state.index >= 0);
}

export function canGoForward(state: NavigationHistory): boolean {
  return state.index >= 0 && state.index < state.entries.length - 1;
}

function capped(state: NavigationHistory): NavigationHistory {
  const excess = state.entries.length - NAVIGATION_HISTORY_LIMIT;
  if (excess <= 0) return state;
  return {
    entries: state.entries.slice(excess),
    index: state.index - excess,
    base: state.base + excess,
  };
}

/**
 * The whole model, as one pure step: "the app is now showing `href`, and the
 * browser calls that entry `browserIndex`".
 *
 * Without an index (an in-memory history, or a browser that lost the state) it
 * falls back to matching the neighbours: landing on the href we recorded one
 * slot either side is a back or a forward, and anything else is a push.
 */
export function visitNavigationHistory(
  state: NavigationHistory,
  href: string,
  browserIndex?: number,
): NavigationHistory {
  if (browserIndex === undefined) return visitWithoutIndex(state, href);

  const position = browserIndex - state.base;
  // Outside the window we recorded: a restored session belonging to a different
  // navigation chain, or a jump past everything we know. Start again from here
  // rather than pretend the gap is navigable.
  if (position < 0 || position > state.entries.length) {
    return { entries: [href], index: 0, base: browserIndex };
  }
  if (position === state.index) {
    // Same slot, new href: a `replace`. It leaves the forward entries alone in
    // the browser, so it must leave them alone here too.
    if (state.entries[position] === href) return state;
    const entries = [...state.entries];
    entries[position] = href;
    return { ...state, entries };
  }
  // A slot we already hold, still holding the same href: an ordinary back,
  // forward, or multi-step go.
  if (position < state.entries.length && state.entries[position] === href) {
    return { ...state, index: position };
  }
  // Anything else is a push. A push after going back discards every forward
  // entry, which is exactly the truncation here.
  return capped({ entries: [...state.entries.slice(0, position), href], index: position, base: state.base });
}

function visitWithoutIndex(state: NavigationHistory, href: string): NavigationHistory {
  if (state.entries[state.index] === href) return state;
  if (state.index > 0 && state.entries[state.index - 1] === href) return { ...state, index: state.index - 1 };
  if (state.entries[state.index + 1] === href) return { ...state, index: state.index + 1 };
  const index = state.index + 1;
  return capped({ entries: [...state.entries.slice(0, index), href], index, base: state.base });
}

/** The subset of `Storage` this module needs, so tests can pass a fake. */
export type NavigationHistoryStorage = Pick<Storage, "getItem" | "setItem">;

/** Tolerant by design: a half-written or older blob is simply no history. */
export function parseNavigationHistory(raw: string | null | undefined): NavigationHistory {
  if (!raw) return EMPTY_NAVIGATION_HISTORY;
  try {
    const parsed = JSON.parse(raw) as Partial<NavigationHistory> | null;
    if (!parsed || !Array.isArray(parsed.entries)) return EMPTY_NAVIGATION_HISTORY;
    const entries = parsed.entries.filter((entry): entry is string => typeof entry === "string");
    if (entries.length !== parsed.entries.length) return EMPTY_NAVIGATION_HISTORY;
    const index = typeof parsed.index === "number" ? parsed.index : -1;
    const base = typeof parsed.base === "number" && parsed.base >= 0 ? parsed.base : 0;
    if (!Number.isInteger(index) || index < -1 || index >= entries.length) return EMPTY_NAVIGATION_HISTORY;
    if (index === -1 && entries.length > 0) return EMPTY_NAVIGATION_HISTORY;
    return capped({ entries, index, base: Number.isInteger(base) ? base : 0 });
  } catch {
    return EMPTY_NAVIGATION_HISTORY;
  }
}

export function readStoredNavigationHistory(storage: NavigationHistoryStorage | undefined): NavigationHistory {
  if (!storage) return EMPTY_NAVIGATION_HISTORY;
  try {
    return parseNavigationHistory(storage.getItem(NAVIGATION_HISTORY_STORAGE_KEY));
  } catch {
    return EMPTY_NAVIGATION_HISTORY;
  }
}

export function writeStoredNavigationHistory(
  storage: NavigationHistoryStorage | undefined,
  state: NavigationHistory,
): void {
  if (!storage) return;
  try {
    storage.setItem(NAVIGATION_HISTORY_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* Private mode or a full quota: the in-memory list still works. */
  }
}

function browserStorage(): NavigationHistoryStorage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

interface NavigationHistoryState extends NavigationHistory {
  visit: (href: string, browserIndex?: number) => void;
}

const useNavigationHistoryStore = create<NavigationHistoryState>((set) => ({
  ...readStoredNavigationHistory(browserStorage()),
  visit: (href, browserIndex) => set((state) => {
    const next = visitNavigationHistory(state, href, browserIndex);
    if (next === state) return state;
    writeStoredNavigationHistory(browserStorage(), { entries: next.entries, index: next.index, base: next.base });
    return next;
  }),
}));

/**
 * `{ back, forward }` availability. Both are plain booleans, so the header
 * re-renders when an arrow lights up and not on every navigation.
 */
export function useNavigationHistory() {
  const back = useNavigationHistoryStore(canGoBack);
  const forward = useNavigationHistoryStore(canGoForward);
  return { canGoBack: back, canGoForward: forward };
}

/** The shape of TanStack's `RouterHistory` that this module actually reads. */
type TrackedLocation = { href: string; state?: { __TSR_index?: number } };
export type TrackedHistory = {
  location: TrackedLocation;
  subscribe: (listener: (opts: { location: TrackedLocation }) => void) => () => void;
};

/**
 * Starts recording. The current location is recorded immediately — `subscribe`
 * only reports what happens next, and the entry you loaded on is the one the
 * first back press has to know about.
 */
export function trackNavigationHistory(history: TrackedHistory): () => void {
  const record = (location: TrackedLocation) => {
    useNavigationHistoryStore.getState().visit(location.href, location.state?.__TSR_index);
  };
  record(history.location);
  return history.subscribe(({ location }) => record(location));
}
